import { Component } from "react";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";

const RELOAD_KEY = "discover_eb_reload_attempts";

export default class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null, info: null };
    }
    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }
    componentDidCatch(error, info) {
        this.setState({ info });
        // Eslint-disable-next-line no-console
        console.error("App error caught:", error, info);
    }
    hardReset = () => {
        try {
            // Clear auth token + welcome flag so the app boots from a
            // pristine state. Keeps user-chosen theme so dark-mode persists.
            localStorage.removeItem("discover_token");
            localStorage.removeItem("discover_welcome_seen_v1");
            sessionStorage.clear();
        } catch { /* ignore */ }
        window.location.href = "/";
    };
    reload = () => {
        // Break the infinite-reload loop: after 2 reloads we push the user
        // back to a clean `/` rather than repeating the crash forever.
        let n = 0;
        try { n = parseInt(sessionStorage.getItem(RELOAD_KEY) || "0", 10); } catch {
            /* ignore */
        }
        if (n >= 2) {
            this.hardReset();
            return;
        }
        try { sessionStorage.setItem(RELOAD_KEY, String(n + 1)); } catch {
            /* ignore */
        }
        window.location.reload();
    };
    render() {
        if (this.state.hasError) {
            const isDev = process.env.NODE_ENV === "development";
            const msg = this.state.error?.message || String(this.state.error || "");
            return (
                <div className="min-h-screen flex items-center justify-center p-6 bg-background">
                    <div className="discover-card p-8 max-w-md text-center space-y-4">
                        <AlertTriangle className="mx-auto text-amber-500" size={48} />
                        <h2 className="text-xl font-black">Oups, une erreur est survenue</h2>
                        <p className="text-sm text-muted-foreground">
                            Pas de panique — rechargez la page pour continuer votre découverte.
                            Si le problème persiste, appuyez sur « Réinitialiser ».
                        </p>
                        {isDev && msg && (
                            <pre className="text-[10px] text-left bg-muted p-2 rounded-xl overflow-auto max-h-40">
                                {msg}
                            </pre>
                        )}
                        <div className="flex gap-2 justify-center flex-wrap">
                            <Button
                                onClick={this.reload}
                                className="rounded-full bg-discover-gradient text-white"
                                data-testid="error-reload-btn">
                                Recharger la page
                            </Button>
                            <Button
                                variant="outline"
                                onClick={() => { this.setState({ hasError: false, error: null, info: null }); }}
                                className="rounded-full"
                                data-testid="error-dismiss-btn">
                                Continuer
                            </Button>
                            <Button
                                variant="ghost"
                                onClick={this.hardReset}
                                className="rounded-full text-xs"
                                data-testid="error-hard-reset-btn">
                                Réinitialiser la session
                            </Button>
                        </div>
                    </div>
                </div>
            );
        }
        return this.props.children;
    }
}
